import React, { useState } from "react";
import { toast } from "sonner";
import { errMsg, FIELD_LABELS, fmt, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { Empty, ErrorBox, Loading, ProtectedTag, StatusBadge } from "../../components/kit";
import { RatingPanel } from "../../components/RatingPanel";

const REASONS: Record<string, string> = {
  blocked_revoked: "The customer revoked consent.", blocked_expired: "Consent expired.",
  blocked_superseded: "The customer changed protected details, trade or area — fresh consent is required.",
  blocked_material_change: "The request changed materially — fresh consent is required.",
  blocked_declined: "You declined this introduction.", blocked_request_cancelled: "The customer cancelled this request.",
  blocked_no_consent: "No consent on record.",
};

function IntroDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const api = useApi();
  const { data, loading, error, reload } = useLoad<any>(`/vendor/introductions/${id}`);
  const respond = async (decision: string) => {
    try { await api("post", `/vendor/introductions/${id}/respond`, { decision }); toast.success(`Introduction ${decision}`); reload(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;
  return (
    <div className="card space-y-4" data-testid="vendor-intro-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-2xl font-bold">{data.request.title}</h3>
        <StatusBadge value={data.status} testId="vendor-intro-status" />
      </div>
      <p className="text-sm">{data.request.summary}</p>
      {data.disclosure === "granted" ? (
        <div className="protected-zone text-sm" data-testid="vendor-disclosed-fields">
          <ProtectedTag text="Disclosed under customer consent" />
          <p className="mt-2 text-xs">Purpose: {data.consent.purpose} · expires {fmt(data.consent.expires_at)}. Use only for this purpose.</p>
          <dl className="mt-2 grid gap-1 sm:grid-cols-2">
            {Object.entries(data.private).map(([k, v]: any) => <div key={k}><dt className="inline font-semibold">{FIELD_LABELS[k]}: </dt><dd className="inline" data-testid={`vendor-disclosed-${k}`}>{v || "—"}</dd></div>)}
          </dl>
        </div>
      ) : (
        <div className="rounded border border-[var(--steel)] bg-[var(--steel-2)] p-3 text-sm" data-testid="vendor-disclosure-blocked">
          <ProtectedTag text="Protected details withheld" />
          <p className="mt-2">{REASONS[data.disclosure] || data.disclosure}</p>
        </div>
      )}
      {data.status === "pending" && (
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-primary" disabled={data.disclosure !== "granted"} onClick={() => respond("accepted")} data-testid="vendor-intro-accept-button">Accept</button>
          <button className="btn btn-danger" onClick={() => respond("declined")} data-testid="vendor-intro-decline-button">Decline</button>
        </div>
      )}
      {data.status === "accepted" && <RatingPanel side="vendor" introId={id} title="Completion & rating" />}
    </div>
  );
}

export function VendorIntros() {
  const { data, loading, error, reload } = useLoad<any[]>("/vendor/introductions");
  const { catName, areaName } = useTaxonomy();
  const [sel, setSel] = useState<string | null>(null);
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data?.length) return <Empty title="No introductions yet">Customers must name your organization and consent before a request appears here.</Empty>;
  return (
    <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
      <ul className="space-y-2" data-testid="vendor-intro-list">
        {data.map((i) => (
          <li key={i.id}>
            <button className={`card w-full text-left transition-colors ${sel === i.id ? "border-[var(--blue)] ring-2 ring-[var(--blue)]" : "hover:border-[var(--blue)]"}`} onClick={() => setSel(i.id)} data-testid={`vendor-intro-item-${i.id}`}>
              <div className="flex items-center justify-between gap-2"><strong>{i.request.title}</strong><StatusBadge value={i.status} /></div>
              <p className="text-xs text-[var(--muted)]">{catName(i.request.category_id)} · {areaName(i.request.area_id)} · {fmt(i.created_at)}</p>
              <p className="mt-1 text-xs">{i.disclosure === "granted" ? "Consent valid" : "Details withheld"}</p>
            </button>
          </li>
        ))}
      </ul>
      {sel ? <IntroDetail key={sel} id={sel} onChanged={reload} /> : <Empty title="Select an introduction" />}
    </div>
  );
}
